// src/pages/PftUploadPage.js
//
// Admin page: upload scanned PFT report PDFs into the PFT extraction job's
// private bucket (one fixed folder per site, see folderForSite). With
// PFT_EXTRACTION_ENABLED, the file list below shows each PDF's extraction state and an
// "Extract next batch" button (PftExtractionPanel); otherwise the page is upload only.

import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { backTarget } from "../utils/backTarget";
import { ArrowLeft, Upload, CheckCircle2, AlertTriangle, FileText, Square, RefreshCw } from "lucide-react";
import { useTenant } from "../context/TenantContext";
import { getActiveSiteId, getActiveTenantId } from "../services/activeTenant";
import { PFT_EXTRACTION_ENABLED, PFT_UPLOAD_BUCKET } from "../config/featureFlags";
import PftExtractionPanel from "./PftExtractionPanel";
import {
  MAX_FILES_PER_SESSION,
  displayName,
  folderForSite,
  formatBytes,
  objectFileName,
  planUpload,
} from "../services/pftUploadPlan";
import { listUploadedNames, loadUploadedNames, uploadPfts } from "../services/pftUploadService";

// How many matching names the "Already uploaded" list shows at once; search narrows it.
const LIST_LIMIT = 300;

export default function PftUploadPage() {
  const navigate = useNavigate();
  // Back: home when opened from the home page, else the Admin Dashboard.
  const back = backTarget(useLocation().state, "/dashboard", "Dashboard");
  const tenant = useTenant() || {};
  const tenantId = tenant.tenantId || getActiveTenantId();
  const siteId = tenant.siteId || getActiveSiteId();
  const siteName =
    ((tenant.sitesMeta || []).find((s) => s.id === siteId) || {}).name || siteId;

  const [plan, setPlan] = useState(null); // { toUpload, skipped }
  const [running, setRunning] = useState(false);
  const [bytesDone, setBytesDone] = useState(0);
  const [statusByName, setStatusByName] = useState({});
  const [result, setResult] = useState(null); // { done, failed }
  const stopRef = useRef(false);
  const inputRef = useRef(null);
  const [uploaded, setUploaded] = useState(null); // stored names in the bucket, or null while loading
  const [uploadedError, setUploadedError] = useState(false);
  const [loadingUploaded, setLoadingUploaded] = useState(false);
  const [filter, setFilter] = useState("");

  const folder = folderForSite(tenantId, siteId);
  const totalBytes = plan ? plan.toUpload.reduce((a, f) => a + f.size, 0) : 0;

  // Everything already in this site's upload folder (names only), from the bucket.
  const refreshUploaded = async () => {
    if (!PFT_UPLOAD_BUCKET || !folder) return;
    setLoadingUploaded(true);
    try {
      const names = await listUploadedNames(tenantId, folder);
      setUploaded(names.sort((a, b) => displayName(a).localeCompare(displayName(b))));
      setUploadedError(false);
    } catch {
      setUploadedError(true);
    } finally {
      setLoadingUploaded(false);
    }
  };

  useEffect(() => {
    refreshUploaded();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, folder]);

  // Warn before leaving while uploads are running.
  useEffect(() => {
    if (!running) return undefined;
    const warn = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [running]);

  const pick = (fileList) => {
    setResult(null);
    setStatusByName({});
    setBytesDone(0);
    // Skip anything already in the bucket (any computer) or remembered by this browser.
    const known = new Set([...loadUploadedNames(tenantId, folder), ...(uploaded || [])]);
    setPlan(planUpload(fileList, known));
  };

  const start = async (files) => {
    if (!files?.length) return;
    stopRef.current = false;
    setRunning(true);
    setResult(null);
    setBytesDone(0);
    let res = { done: [], failed: [] };
    try {
      res = await uploadPfts({
        files,
        tenantId,
        batchId: folder,
        onFile: ({ name, status }) => setStatusByName((s) => ({ ...s, [name]: status })),
        onBytes: setBytesDone,
        shouldStop: () => stopRef.current,
      });
    } catch {
      res = { done: [], failed: [{ name: "(this session)", reason: "upload could not run; try again" }] };
    } finally {
      // Always end the session and show a result, whatever happened.
      setRunning(false);
      setResult(res);
      refreshUploaded();
    }
  };

  const retryFailed = () => {
    if (!plan || !result?.failed?.length) return;
    const failedNames = new Set(result.failed.map((f) => f.name));
    start(plan.toUpload.filter((f) => failedNames.has(objectFileName(f.name))));
  };

  const counts = Object.values(statusByName).reduce((acc, s) => ({ ...acc, [s]: (acc[s] || 0) + 1 }), {});
  const pct = totalBytes ? Math.min(100, Math.round((bytesDone / totalBytes) * 100)) : 0;
  const q = filter.trim().toLowerCase();
  const matches = (uploaded || []).filter((n) => !q || displayName(n).toLowerCase().includes(q));

  return (
    <div className="min-h-screen bg-[#0b0820] text-slate-100 px-4 py-6 sm:px-8">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate(back.to)}
            disabled={running}
            className="inline-flex items-center gap-1.5 px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-sm text-slate-200 transition disabled:opacity-40"
          >
            <ArrowLeft className="w-4 h-4" />
            {back.label}
          </button>
          <div>
            <h1 className="text-xl font-semibold tracking-tight">Upload PFT PDFs</h1>
            <p className="text-[12px] text-slate-400">
              Send scanned PFT report PDFs to secure cloud storage for extraction. Up to{" "}
              {MAX_FILES_PER_SESSION} files at a time.
            </p>
            <p className="text-[12px] text-slate-300 mt-0.5">
              Uploading for site: <span className="font-semibold">{siteName}</span>
            </p>
          </div>
        </div>

        {!PFT_UPLOAD_BUCKET || !folder ? (
          <div className="rounded-2xl border border-amber-300/30 bg-amber-500/10 p-4 text-sm text-amber-100">
            PFT upload storage is not configured for this environment.
          </div>
        ) : (
          <div className="rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-5 space-y-5">
            <div className="grid gap-2">
              <input
                ref={inputRef}
                id="pft-files"
                type="file"
                accept="application/pdf,.pdf"
                multiple
                disabled={running}
                onChange={(e) => pick(e.target.files)}
                className="hidden"
              />
              <button
                onClick={() => inputRef.current?.click()}
                disabled={running}
                className="inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-dashed border-white/20 bg-white/[0.04] hover:bg-white/[0.08] text-sm text-slate-200 transition disabled:opacity-40"
              >
                <FileText className="w-4 h-4" />
                Choose PDF files
              </button>
              <p className="text-[12px] text-slate-400">
                Pick up to {MAX_FILES_PER_SESSION} PDFs at a time. Files that are already uploaded are
                skipped automatically, so it's safe to pick some again.
              </p>
            </div>

            {plan && (
              <div className="grid gap-2 text-sm">
                <div className="text-slate-200">
                  <span className="font-semibold">{plan.toUpload.length}</span> files ready (
                  {formatBytes(totalBytes)})
                  {plan.skipped.length > 0 && (
                    <span className="text-amber-300"> · {plan.skipped.length} left out</span>
                  )}
                </div>
                {plan.skipped.length > 0 && (
                  <details className="text-[12px] text-slate-400">
                    <summary className="cursor-pointer">Why some files were left out</summary>
                    <ul className="mt-1 max-h-40 overflow-auto pl-4 list-disc">
                      {plan.skipped.map((s, i) => (
                        <li key={`${s.name}-${i}`}>
                          {s.name}: {s.reason}
                        </li>
                      ))}
                    </ul>
                  </details>
                )}
              </div>
            )}

            {plan && plan.toUpload.length > 0 && (
              <div className="flex flex-wrap items-center gap-3">
                {!running ? (
                  <button
                    onClick={() => start(plan.toUpload)}
                    disabled={!!result}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-gradient-to-r from-purple-500/40 to-blue-500/40 border border-purple-300/30 hover:from-purple-500/60 hover:to-blue-500/60 text-sm font-medium text-white transition disabled:opacity-40"
                  >
                    <Upload className="w-4 h-4" />
                    Upload {plan.toUpload.length} files
                  </button>
                ) : (
                  <button
                    onClick={() => {
                      stopRef.current = true;
                    }}
                    className="inline-flex items-center gap-1.5 px-4 py-2 rounded-xl bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-sm text-slate-200 transition"
                  >
                    <Square className="w-4 h-4" />
                    Stop after current files
                  </button>
                )}
                {running && (
                  <span className="text-[12px] text-amber-300">
                    Keep this tab open and the computer awake until it finishes.
                  </span>
                )}
              </div>
            )}

            {(running || result) && plan && (
              <div className="grid gap-2">
                <div className="h-2.5 rounded-full border border-white/15 bg-white/[0.06] overflow-hidden" aria-label={`${pct}% uploaded`}>
                  <div className="h-full bg-emerald-500 transition-all" style={{ width: `${pct}%` }} />
                </div>
                <div className="text-[12px] text-slate-300 tabular-nums">
                  {pct}% · {counts.done || 0} uploaded · {counts.uploading || 0} in progress ·{" "}
                  {counts.failed || 0} failed · {plan.toUpload.length - Object.keys(statusByName).length} waiting
                </div>
              </div>
            )}

            {result && (
              <div
                className={`rounded-xl border p-4 text-sm ${
                  result.failed.length
                    ? "border-amber-300/30 bg-amber-500/10 text-amber-100"
                    : "border-emerald-300/30 bg-emerald-500/10 text-emerald-100"
                }`}
              >
                <div className="flex items-center gap-2 font-medium">
                  {result.failed.length ? <AlertTriangle className="w-4 h-4" /> : <CheckCircle2 className="w-4 h-4" />}
                  {result.done.length} uploaded
                  {result.failed.length ? `, ${result.failed.length} failed` : ""}
                </div>
                {result.failed.length > 0 && (
                  <>
                    <ul className="mt-2 max-h-40 overflow-auto pl-4 list-disc text-[12px]">
                      {result.failed.map((f, i) => (
                        <li key={`${f.name}-${i}`}>
                          {f.name}: {f.reason}
                        </li>
                      ))}
                    </ul>
                    <button
                      onClick={retryFailed}
                      className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.06] border border-white/15 hover:bg-white/[0.1] text-[12px] text-slate-100 transition"
                    >
                      Retry failed files
                    </button>
                  </>
                )}
                <p className="mt-2 text-[12px] opacity-80">
                  To upload more, choose the next set of PDFs. Files already uploaded are skipped or refused, never duplicated.
                </p>
              </div>
            )}
          </div>
        )}

        {PFT_UPLOAD_BUCKET && PFT_EXTRACTION_ENABLED && folder && (
          <PftExtractionPanel
            tenantId={tenantId}
            siteId={siteId}
            folder={folder}
            uploaded={uploaded}
            uploadedError={uploadedError}
            loadingUploaded={loadingUploaded}
            onRefresh={refreshUploaded}
          />
        )}

        {PFT_UPLOAD_BUCKET && !PFT_EXTRACTION_ENABLED && (
          <div className="mt-6 rounded-2xl border border-white/10 bg-white/[0.03] backdrop-blur-2xl p-5 space-y-3">
            <div className="flex flex-wrap items-center gap-3">
              <h2 className="text-sm font-semibold">
                Already uploaded{uploaded ? ` (${uploaded.length})` : ""}
              </h2>
              <button
                onClick={refreshUploaded}
                disabled={loadingUploaded}
                className="ml-auto inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white/[0.04] border border-white/10 hover:bg-white/[0.08] text-[12px] text-slate-200 transition disabled:opacity-40"
              >
                <RefreshCw className={`w-3.5 h-3.5 ${loadingUploaded ? "animate-spin" : ""}`} />
                Refresh
              </button>
            </div>
            {uploadedError ? (
              <p className="text-[12px] text-amber-300">Couldn't load the list of uploaded files. Try Refresh.</p>
            ) : uploaded === null ? (
              <p className="text-[12px] text-slate-400">Loading…</p>
            ) : uploaded.length === 0 ? (
              <p className="text-[12px] text-slate-400">Nothing uploaded yet.</p>
            ) : (
              <>
                <input
                  id="pft-uploaded-search"
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder="Search file names"
                  className="w-full px-3 py-2 rounded-xl bg-white/[0.04] border border-white/10 text-sm text-slate-100 focus:outline-none focus:ring-2 focus:ring-purple-400/50"
                />
                <ul className="max-h-72 overflow-auto rounded-xl border border-white/10 divide-y divide-white/5 text-[12px]">
                  {matches.slice(0, LIST_LIMIT).map((n) => (
                    <li key={n} className="px-3 py-1.5 text-slate-200 break-all">
                      {displayName(n)}
                    </li>
                  ))}
                </ul>
                <p className="text-[11px] text-slate-400">
                  {matches.length > LIST_LIMIT
                    ? `Showing ${LIST_LIMIT} of ${matches.length} matching files. Search to narrow the list.`
                    : `${matches.length} file${matches.length === 1 ? "" : "s"}${q ? " match" : ""}.`}{" "}
                  Names only: files can't be opened from this page.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
